<template>
  <div class="tile is-ancestor footer-padding">
    <div class="tile is-parent is-vertical is-6">
      <tile title="Basic Information" subtitle="Name and contact info">

        <b-field label="Email" horizontal>
          <b-input disabled v-model="user.email" />
        </b-field>

        <b-field :addons="false">
          <ImageUpload @input="(f) => newProfileImage = f" :isSaved="!newProfileImage"
            :image="newProfileImage || (profile.image && profile.image.file)"
            accept="image/jpeg,image/png,image/jpg"
            label="Profile Image (jpeg, png, jpg)" horizontal/>
        </b-field>

        <b-field label="First Name" horizontal :type="dirty && errors.first_name ? 'is-danger': ''"
            :message="dirty && errors.first_name ? 'This field is required' : null">
          <b-input v-model="edittedProfile.first_name" />
        </b-field>

        <b-field label="Middle Name" horizontal>
          <b-input placeholder="Leave empty if you don't have one"
            v-model="edittedProfile.middle_name" />
        </b-field>

        <b-field label="Last Name" horizontal :type="dirty && errors.last_name ? 'is-danger': ''"
            :message="dirty && errors.last_name ? 'This field is required' : null">
          <b-input v-model="edittedProfile.last_name" />
        </b-field>

        <b-field label="Phone number" horizontal
          :type="dirty && errors.phone_number ? 'is-danger': ''"
          :message="dirty && errors.phone_number ? 'This field is required' : null">
          <b-input type="number" v-model="edittedProfile.phone_number"
            placeholder="Add your Country Code"/>
        </b-field>

        <b-field label="WhatsApp" horizontal>
          <b-input type="number" v-model="edittedProfile.whatsapp_number"
            placeholder="Add your Country Code"/>
        </b-field>

        <b-field label="Skype ID" horizontal>
          <b-input v-model="edittedProfile.skype_id" />
        </b-field>
      </tile>

      <tile title="Desired Employment"
        :subtitle="edittedProfile.desired_employment_positions.join(', ')"
        v-if="excludeForTypes(['owner', 'charterer'])">

        <b-field label="Countries willing to work at" horizontal
          :type="dirty && errors.employment_countries ? 'is-danger': ''"
          :message="dirty && errors.employment_countries
            ? 'This field is required' : null">
          <AutoCompleteMultiple
              :options="this.countries"
              v-model="edittedProfile.employment_countries"/>
        </b-field>


        <b-field label="Employment Positions" horizontal
          :type="dirty && errors.desired_employment_positions ? 'is-danger': ''"
          :message="dirty && errors.desired_employment_positions
            ? 'This field is required' : null">
          <TagSelect :items="employmentPositionsPerType"
            :activeList="edittedProfile.desired_employment_positions"
            @toggle="(s) => toggleDesiredEmploymentPosition(s)"/>
        </b-field>

        <b-field label="Availability" horizontal>
          <b-select placeholder="Select a status"
            v-model="edittedProfile.employment_availability" expanded>
            <option
              v-for="option in availabilityChoices"
              :value="option"
              :key="option">
              {{ option }}
            </option>
          </b-select>
        </b-field>

        <b-field label="Diary (mark the days that you are NOT available)"
          v-if="onlyForTypes(['crew'])">
          <div class="availability-picker">
            <b-datepicker
              :min-date="new Date()"
              inline
              @input="toggleAvailabilityDate"
              :events="edittedProfile.unavailability_dates.map(
                d => ({date: new Date(d.date), type:'is-danger'}))"
              indicators="bars">
            </b-datepicker>
          </div>
        </b-field>
      </tile>

      <tile title="Diplomas/Licences"
        subtitle="Add your diplomas (only for Skippers, Captains)"
        v-if="user.type === 'crew'">
        <span class="has-text-secondary" v-if="errors.diplomas">
          You are required to upload at least one diploma
        </span>
        <b-modal :active.sync="isDiplomaModalOpen" has-modal-card>
          <DiplomaForm @diplomaSaved="(v) => edittedProfile.diplomas.push(v)" />
        </b-modal>
        <div class="columns" v-for="(diploma, index) in edittedProfile.diplomas"
          :key="index">
          <div class="column">
            <b>{{diploma.name}}</b>
            <span class="has-text-danger" v-if="diploma.tempFile"> Press save to upload</span>
            <a :href="diploma.file.file" target="_blank" v-else> Existing file</a>
          </div>
          <div class="column is-one-fifth">
            <a @click="edittedProfile.diplomas.splice(index, 1)">
              <b-icon type="is-danger" icon="close"/>
            </a>
          </div>
        </div>

        <div class="right-align">
          <button @click="isDiplomaModalOpen = true"
            class='button is-primary'>
            <b-icon icon="plus" />
            <span>Add new diploma</span>
          </button>
        </div>
      </tile>

      <tile title="Company info"
        subtitle="Fill in the company details where applicable"
        v-if="user.type === 'owner'">
        <b-field label="Operation type" horizontal>
          <b-radio v-model="edittedProfile.company_type"
            native-value="broker" type="is-black">Broker</b-radio>
          <b-radio v-model="edittedProfile.company_type"
            native-value="company" type="is-black">Company</b-radio>
          <b-radio v-model="edittedProfile.company_type"
            native-value="private_owner" type="is-black">Private Owner</b-radio>
        </b-field>

        <b-field label="Company name" horizontal
          v-if="edittedProfile.company_type !== 'private_owner'">
          <b-input v-model="edittedProfile.company_name" />
        </b-field>

        <b-field label="Position in company" horizontal
          v-if="edittedProfile.company_type !== 'private_owner'">
          <b-input v-model="edittedProfile.company_position" />
        </b-field>

        <b-field label="Company address" horizontal
          v-if="edittedProfile.company_type !== 'private_owner'">
          <b-input v-model="edittedProfile.company_address" />
        </b-field>

         <b-field label="Website" horizontal
          :type="dirty && errors.company_website ? 'is-danger': ''"
          :message="dirty && errors.company_website
            ? 'This field is required' : null">
          <b-input v-model="edittedProfile.company_website" />
        </b-field>
      </tile>

      <tile title="Charter Info"
        subtitle="Fill in your starting and ending dates"
        v-if="user.type === 'charterer'">
        <div v-if="dirty && errors.charterer_dates" class="has-text-secondary">
          Make sure you have at least one and all of them are filled
        </div>
        <div v-for="(d, index) in edittedProfile.charterer_dates" :key="index"
          class="charterer-dates">
          <b-field label="Start date">
            <b-datepicker placeholder="Click to select..."
                :value="d.start && new Date(d.start)"
                @input="(v) => d.start = $moment(v).format('YYYY-MM-DD')"
                icon="calendar-today"/>
          </b-field>

          <b-field label="End date">
            <b-datepicker placeholder="Click to select..."
                :value="d.end && new Date(d.end)"
                @input="(v) => d.end = $moment(v).format('YYYY-MM-DD')"
                icon="calendar-today"/>
          </b-field>

          <a @click="edittedProfile.charterer_dates.splice(index, 1)">
            <b-icon type="is-danger" icon="close"/>
          </a>
        </div>
        <button class="button is-primary"
          @click="edittedProfile.charterer_dates.push({start: null, end: null})">
          Add
        </button>
      </tile>

    </div>


    <div class="tile is-parent is-vertical is-6">
      <tile title="Extended Information" subtitle="Tell us a bit more about yourself">
        <b-field label="Gender" horizontal class="radio-button"
          :message="dirty && errors.gender ? 'This field is required' : null"
          :type="dirty && errors.gender ? 'is-danger': ''">
          <b-radio v-model="edittedProfile.gender"
            native-value="male" type="is-black">Male</b-radio>
          <b-radio v-model="edittedProfile.gender"
            native-value="female" type="is-black">Female</b-radio>
        </b-field>

        <b-field label="Nationality" horizontal
          :message="dirty && errors.nationality ? 'This field is required' : null"
          :type="dirty && errors.nationality ? 'is-danger': ''">
          <AutoComplete
              :options="this.nationalities"
              :initialValue="edittedProfile.nationality"
              @select="option => edittedProfile.nationality = option"/>
        </b-field>

        <b-field label="Current country" horizontal
          :message="dirty && errors.current_country ? 'This field is required' : null"
          :type="dirty && errors.current_country ? 'is-danger': ''">
          <AutoComplete
              :options="this.countries"
              :initialValue="edittedProfile.current_country"
              @select="option => edittedProfile.current_country = option"/>
        </b-field>

        <b-field label="Current harbor" horizontal
          v-if="onlyForTypes(['crew', 'on_dock', 'charterer', 'diver'])">
          <b-input v-model="edittedProfile.current_harbor" />
        </b-field>

        <b-field label="Professional experience (years)"
          horizontal v-if="excludeForTypes(['charterer'])"
          :message="dirty && errors.experience_years ? 'This field is required': null"
          :type="dirty && errors.experience_years ? 'is-danger': null">
          <b-input type="number" :value="edittedProfile.experience_years"
            @input="v=> edittedProfile.experience_years=(v||null)" />
        </b-field>

        <b-field label="Payment (€)" horizontal
          v-if="onlyForTypes(['crew', 'on_dock', 'charterer'])">
          <b-input type="number" :value="edittedProfile.salary"
            @input="v=> edittedProfile.salary=(v||null)"/>
        </b-field>

        <b-field label="Marital status" horizontal v-if="onlyForTypes(['crew'])"
          :message="dirty && errors.marital_status ? 'This field is required': null"
          :type="dirty && errors.marital_status ? 'is-danger': null">
          <b-select placeholder="Select a status" v-model="edittedProfile.marital_status" expanded>
            <option
              v-for="option in ['Single', 'In a Relationship', 'Married', 'Divorced']"
              :value="option"
              :key="option">
              {{ option }}
            </option>
          </b-select>
        </b-field>

        <b-field label="Birthday" horizontal :type="dirty && errors.birthday ? 'is-danger': ''"
            :message="dirty && errors.birthday ? 'This field is required' : null">
          <b-datepicker placeholder="Click to select..."
            :value="edittedProfile.birthday && new Date(edittedProfile.birthday)"
            @input="(d) => edittedProfile.birthday = $moment(d).format('YYYY-MM-DD')"
            :focused-date="new Date(edittedProfile.birthday || '2010-5-15')"
            :max-date="new Date()"
            icon="calendar-today"/>
        </b-field>

        <b-field label="Smoker" horizontal class="radio-button" v-if="onlyForTypes(['crew'])"
          :type="dirty && errors.smoker ? 'is-danger': ''"
          :message="dirty && errors.smoker ? 'This field is required' : null">
          <b-radio v-model="edittedProfile.smoker"
            :native-value="true" type="is-black">Yes</b-radio>
          <b-radio v-model="edittedProfile.smoker"
            :native-value="false" type="is-black">No</b-radio>
        </b-field>

        <b-field label="Visible tatoos" horizontal class="radio-button"
          :type="dirty && errors.visible_tatoos ? 'is-danger': ''"
          :message="dirty && errors.visible_tatoos ? 'This field is required' : null"
          v-if="onlyForTypes(['crew'])">
          <b-radio v-model="edittedProfile.visible_tatoos"
            :native-value="true" type="is-black">Yes</b-radio>
          <b-radio v-model="edittedProfile.visible_tatoos"
            :native-value="false" type="is-black">No</b-radio>
        </b-field>
      </tile>

      <tile title="Passports/Visa" v-if="onlyForTypes(['crew'])">
        <b-field label="Visas" horizontal>
          <TagSelect :items="visas"
            :activeList="edittedProfile.visas"
            @toggle="(s) => toggleVisa(s)"/>
        </b-field>

        <b-field label="Passports" horizontal v-if="edittedProfile.passports.length">
          <div>
            <div class="box" v-for="(passport, index) in edittedProfile.passports"
              :key="passport.country">
              <div class="right-align">
                <a @click="edittedProfile.passports.splice(index, 1)">
                  <b-icon type="is-danger" icon="close"/>
                </a>
              </div>
              <b-field label="Issuer" horizontal>
                <AutoComplete
                  :options="countries"
                  :initialValue="passport.country"
                  @select="option => passport.country = option"/>
              </b-field>
              <b-field label="Valid until" horizontal>
                <b-datepicker placeholder="Click to select..."
                  :value="passport.valid_to && new Date(passport.valid_to )"
                  @input="(d) => passport.valid_to = $moment(d).format('YYYY-MM-DD')"
                  :focused-date="new Date(passport.valid_to || '2020-5-15')"
                  icon="calendar-today"/>
              </b-field>
            </div>
          </div>
        </b-field>

        <div class="right-align">
          <button @click="edittedProfile.passports.push({country:'', valid_to: null})"
            :disabled="edittedProfile.passports.find(p => !p.country || !p.valid_to)"
            class='button is-primary'>
            <b-icon icon="plus" />
            <span>Add new passport</span>
          </button>
        </div>
      </tile>

      <tile title="Vessels" subtitle="Add information about your vessels"
        v-if="onlyForTypes(['owner', 'charterer']) && edittedProfile.company_type !== 'broker'">
        <span class="has-text-secondary" v-if="dirty && errors.vessels">
          It is required to add at least one vessel
        </span>
        <span class="has-text-secondary" v-if="hasVesselChanges">
          Press <b>Save</b> to apply
        </span>
        <b-modal :active.sync="isVesselModalOpen" has-modal-card>
          <VesselForm @vesselSaved="(v) => edittedProfile.vessels.push(v)" :userType="user.type"/>
        </b-modal>
        <div class="columns" v-for="(vessel, index) in edittedProfile.vessels"
          :key="index">
          <div class="column">
          <div><span>
            <b>{{vessel.ownership_type}} vessel of type:</b>
            {{vessel.type}} - {{vessel.sub_type}} - {{vessel.size_ft}}ft</span></div>
          </div>
          <div class="column is-one-fifth">
            <a @click="edittedProfile.vessels.splice(index, 1)">
              <b-icon type="is-danger" icon="close"/>
            </a>
          </div>
        </div>

        <div class="right-align">
          <button @click="isVesselModalOpen = true"
            :disabled="edittedProfile.vessels.length === 10"
            class='button is-primary'>
            <b-icon icon="plus" />
            <span>Add new vessel</span>
          </button>
        </div>

      </tile>

      <tile title="About me" subtitle="Write some words about yourself">
        <b-input maxlength="500" type="textarea" v-model="edittedProfile.intro"/>
      </tile>

    </div>
    <Footer>
      <button :class="[
        'button', 'is-medium',
        'is-primary', loading && 'is-loading']"
        :disabled="!hasChanges"
        @click="save">
        Save
      </button>
    </Footer>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import Tile from '@/components/Tile';
import Footer from '@/components/Footer';
import ImageUpload from '@/components/ImageUpload';
import TagSelect from '@/components/TagSelect';
import AutoComplete from '@/components/AutoComplete';
import AutoCompleteMultiple from '@/components/AutoCompleteMultiple';
import VesselForm from '@/views/common/VesselForm';
import DiplomaForm from '@/views/common/DiplomaForm';

export default {
  components: {
    Tile,
    Footer,
    ImageUpload,
    TagSelect,
    AutoComplete,
    AutoCompleteMultiple,
    VesselForm,
    DiplomaForm,
  },
  data() {
    return {
      loading: false,
      date_exceptions: [],
      edittedProfile: {},
      newProfileImage: null,
      dirty: false,
      isVesselModalOpen: false,
      isDiplomaModalOpen: false,
      requiredFieldsForType: {
        crew: ['first_name', 'last_name', 'birthday', 'desired_employment_positions', 'phone_number', 'current_country',
          'employment_countries', 'nationality', 'gender', 'marital_status', 'smoker', 'visible_tatoos'],
        owner: ['first_name', 'last_name', 'phone_number', 'current_country', 'nationality', 'gender', 'experience_years', 'birthday'],
        diver: ['first_name', 'last_name', 'phone_number', 'current_country', 'experience_years', 'desired_employment_positions',
          'employment_countries', 'current_country', 'nationality', 'gender'],
        charterer: ['first_name', 'last_name', 'phone_number', 'vessels', 'charterer_dates', 'current_country'],
        on_dock: ['first_name', 'last_name', 'birthday', 'phone_number', 'current_country', 'gender', 'nationality',
          'employment_countries', 'experience_years'],
      },
    };
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      profile: state => state.auth.user.profile,
      nationalities: state => state.content.nationalities,
      employmentPositions: state => state.content.employment_positions,
      countries: state => state.content.countries,
      visas: state => state.content.visas,
      availabilityChoices: state => state.content.availability_choices,
    }),
    hasChanges() {
      return this.newProfileImage || !isEqual(this.profile, this.edittedProfile);
    },
    hasVesselChanges() {
      return !isEqual(this.profile.vessels, this.edittedProfile.vessels);
    },
    employmentPositionsPerType() {
      const keys = {
        crew: 'all',
        owner: [],
        diver: ['Diver'],
        charterer: [],
        on_dock: ['Shipyard/Dock Services'],
      }[this.user.type];
      if (keys === 'all') return this.employmentPositions;
      return keys;
    },
    errors() {
      let requiredFields = this.requiredFieldsForType[this.user.type];

      if (this.user.type === 'crew' && (
        this.edittedProfile.desired_employment_positions.indexOf('Skipper') !== -1
        || this.edittedProfile.desired_employment_positions.indexOf('Captain') !== -1
      )) {
        requiredFields = requiredFields.concat(['diplomas']);
      }

      if (this.user.type === 'owner' && this.edittedProfile.company_type !== 'broker') {
        requiredFields = requiredFields.concat(['vessels']);
      }

      if (this.user.type === 'owner' && this.edittedProfile.company_type !== 'private_owner') {
        requiredFields = requiredFields.concat(['company_website']);
      }

      const errors = requiredFields.reduce(
        (acc, field) => {
          acc[field] = Array.isArray(this.edittedProfile[field])
            ? !this.edittedProfile[field].length
            : this.edittedProfile[field] === null || this.edittedProfile[field] === '';
          return acc;
        }, {},
      );

      if (requiredFields.indexOf('charterer_dates') !== -1 && this.edittedProfile.charterer_dates.length) {
        errors.charterer_dates = this.edittedProfile.charterer_dates.some(d => !(d.start && d.end));
      }

      return errors;
    },
  },

  methods: {
    ...mapActions({
      updateProfile: 'auth/updateProfile',
    }),
    onlyForTypes(types) {
      return types.indexOf(this.user.type) !== -1;
    },
    excludeForTypes(types) {
      return types.indexOf(this.user.type) === -1;
    },
    toggleDesiredEmploymentPosition(position) {
      const index = this.edittedProfile.desired_employment_positions.indexOf(position);
      if (index === -1) {
        this.edittedProfile.desired_employment_positions.push(position);
      } else {
        this.edittedProfile.desired_employment_positions.splice(index, 1);
      }
    },
    toggleVisa(visa) {
      const index = this.edittedProfile.visas.indexOf(visa);
      if (index === -1) {
        this.edittedProfile.visas.push(visa);
      } else {
        this.edittedProfile.visas.splice(index, 1);
      }
    },
    toggleAvailabilityDate(d) {
      const date = this.$moment(d).format('YYYY-MM-DD');
      const index = this.edittedProfile.unavailability_dates.findIndex(e => e.date === date);
      if (index === -1) {
        this.edittedProfile.unavailability_dates.push({ date });
      } else {
        this.edittedProfile.unavailability_dates.splice(index, 1);
      }
    },
    initialize() {
      this.edittedProfile = cloneDeep(this.profile);
      this.newProfileImage = null;
    },
    async saveImage() {
      const data = new FormData();
      data.append('category', 'profile_image');
      data.append('file', this.newProfileImage);
      const resp = await this.$http.post('files/', data, { 'Content-Type': 'multipart/form-data' });
      this.edittedProfile.image = resp.data.id;
    },
    async handleDiplomas() {
      this.edittedProfile.diplomas = await Promise.all(
        this.edittedProfile.diplomas.map(async (d) => {
          if (!d.tempFile) {
            return Promise.resolve({ ...d, file: d.file.id });
          }
          const data = new FormData();
          data.append('category', 'diploma');
          data.append('file', d.tempFile);
          const resp = await this.$http.post('files/', data, { 'Content-Type': 'multipart/form-data' });
          return { ...d, file: resp.data.id, tempFile: undefined };
        }),
      );
    },
    async save() {
      this.dirty = true;
      const hasError = Object.values(this.errors).some(v => v);
      if (hasError) {
        this.$toast.open({
          message: 'There are missing required fields',
          type: 'is-danger',
        });
        if (this.edittedProfile.profile_saved) {
          // don't save missing information in completed profiles
          return;
        }
      }
      this.loading = true;
      if (this.newProfileImage) {
        try {
          await this.saveImage();
        } catch (e) {
          this.$toast.open({
            message: 'Could not save the new profile image',
            type: 'is-danger',
          });
          this.loading = false;
          return;
        }
      } else {
        delete this.edittedProfile.image;
      }
      try {
        await this.handleDiplomas();
      } catch (e) {
        this.$toast.open({
          message: 'Could not save the diplomas',
          type: 'is-danger',
        });
        this.loading = false;
        return;
      }

      try {
        const payload = omit(this.edittedProfile, ['certificates', 'qualifications', 'skills', 'cv']);
        payload.profile_saved = !hasError;
        await this.updateProfile(payload);
        this.initialize();
      } catch (e) {
        this.$toast.open({
          message: 'Could not save the personal info',
          type: 'is-danger',
        });
      }
      this.loading = false;
    },
  },
  created() {
    this.initialize();
  },
};
</script>

<style lang="scss" scoped>
.right-align {
  justify-content: flex-end;
  display: flex;
  margin-bottom: 0.5rem
}

/deep/ .field-label {
  flex-grow: 2
}

/deep/ .datepicker-row .is-selected {
  background-color: white !important;
  color: #4a4a4a !important;
}

.availability-picker {
  display: flex;
  justify-content: center;
}

.charterer-dates {
  display: flex;
  align-items: center;
  flex-direction: row;
}
</style>
